import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, CheckCircle2, XCircle, StopCircle, Trash2 } from "lucide-react";
import axiosInstance from "../../services/API";

// status: 'idle' | 'running' | 'paused' | 'done'
const STATUS = { IDLE: "idle", RUNNING: "running", PAUSED: "paused", DONE: "done" };

const DownloadMyVideos = () => {
    const navigate = useNavigate();
    const { User_profile } = useSelector((state) => state.AccountProfile);

    const [status, setStatus] = useState(STATUS.IDLE);
    const [log, setLog] = useState([]);           // array of { id, description, thumbnail_url, state }
    const [progress, setProgress] = useState({ page: 0, totalPages: 0, downloaded: 0, failed: 0, total: 0 });
    const [error, setError] = useState(null);

    const stopRef = useRef(false);

    // ── Delete All state ─────────────────────────────────────────────────────
    const [deleteStatus, setDeleteStatus] = useState(STATUS.IDLE);
    const [deleteLog, setDeleteLog] = useState([]);
    const [deleteProgress, setDeleteProgress] = useState({ page: 0, totalPages: 0, deleted: 0, failed: 0, total: 0 });
    const [deleteError, setDeleteError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const deleteStopRef = useRef(false);

    const addLog = (entry) => setLog((prev) => [entry, ...prev].slice(0, 200));

    const updateLogState = (id, state) =>
        setLog((prev) => prev.map((e) => (e.id === id ? { ...e, state } : e)));

    // ── Download one video ───────────────────────────────────────────────────
    const downloadOne = async (post) => {
        if (!post.download_enabled) return "skipped";

        updateLogState(post.id, "downloading");
        try {
            const res = await fetch(post.download_url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            const safeName = (post.post_description || post.id)
                .replace(/[^a-z0-9]/gi, "_")
                .slice(0, 80);
            a.download = `${safeName}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
            updateLogState(post.id, "done");
            return "done";
        } catch {
            updateLogState(post.id, "error");
            return "error";
        }
    };

    // ── Main auto script ─────────────────────────────────────────────────────
    const runScript = async () => {
        if (!User_profile?.id) return;
        stopRef.current = false;
        setStatus(STATUS.RUNNING);
        setLog([]);
        setError(null);
        setProgress({ page: 0, totalPages: 0, downloaded: 0, failed: 0, total: 0 });

        let nextUrl = null;
        let pageNum = 0;
        let totalCount = 0;
        let downloaded = 0;
        let failed = 0;
        let totalPages = 0;
        let pageSize = 12;

        try {
            // ── page loop ────────────────────────────────────────────────────
            do {
                if (stopRef.current) break;

                pageNum += 1;

                // fetch the page
                let data;
                if (pageNum === 1) {
                    const res = await axiosInstance.get(`/post/profile/${User_profile.id}/public-posts/`);
                    data = res.data;
                } else {
                    const url = new URL(nextUrl);
                    const rel = (url.pathname + url.search).replace("/api", "");
                    const res = await axiosInstance.get(rel);
                    data = res.data;
                }

                totalCount = data.count || data.results?.total_count || totalCount;
                nextUrl = data.next;
                const posts = data.results?.posts || [];
                if (pageNum === 1 && posts.length > 0) pageSize = posts.length;
                totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : totalPages;

                setProgress((p) => ({ ...p, page: pageNum, totalPages, total: totalCount }));

                // add all posts from this page to log immediately
                setLog((prev) => [
                    ...posts.map((post) => ({
                        id: post.id,
                        description: post.post_description,
                        thumbnail_url: post.thumbnail_url,
                        state: post.download_enabled ? "queued" : "disabled",
                    })),
                    ...prev,
                ].slice(0, 500));

                // ── video loop for this page ──────────────────────────────────
                for (const post of posts) {
                    if (stopRef.current) break;
                    const result = await downloadOne(post);
                    if (result === "done") {
                        downloaded += 1;
                    } else if (result === "error") {
                        failed += 1;
                    }
                    setProgress((p) => ({ ...p, downloaded, failed }));
                    // small gap between downloads
                    await new Promise((r) => setTimeout(r, 400));
                }

                if (stopRef.current) break;

                // small gap between pages
                if (nextUrl) await new Promise((r) => setTimeout(r, 600));

            } while (nextUrl);

        } catch (err) {
            setError("Something went wrong while fetching pages. Try again.");
        }

        setStatus(stopRef.current ? STATUS.PAUSED : STATUS.DONE);
    };

    const stopScript = () => {
        stopRef.current = true;
    };

    const resetScript = () => {
        setStatus(STATUS.IDLE);
        setLog([]);
        setProgress({ page: 0, totalPages: 0, downloaded: 0, failed: 0, total: 0 });
        setError(null);
    };

    // ── Delete one video ─────────────────────────────────────────────────────
    const deleteOne = async (post) => {
        setDeleteLog((prev) => prev.map((e) => (e.id === post.id ? { ...e, state: "deleting" } : e)));
        try {
            await axiosInstance.delete(`post/delete/${post.id}/`);
            setDeleteLog((prev) => prev.map((e) => (e.id === post.id ? { ...e, state: "done" } : e)));
            return "done";
        } catch {
            setDeleteLog((prev) => prev.map((e) => (e.id === post.id ? { ...e, state: "error" } : e)));
            return "error";
        }
    };

    // ── Delete all script ────────────────────────────────────────────────────
    // Always re-fetches page 1 after each batch because deleting shifts the list.
    const runDeleteScript = async () => {
        if (!User_profile?.id) return;
        deleteStopRef.current = false;
        setDeleteStatus(STATUS.RUNNING);
        setDeleteLog([]);
        setDeleteError(null);
        setDeleteProgress({ page: 0, totalPages: 0, deleted: 0, failed: 0, total: 0 });

        let round = 0;
        let initialTotal = 0;
        let deleted = 0;
        let failed = 0;
        let pageSize = 12;

        try {
            while (true) {
                if (deleteStopRef.current) break;

                round += 1;

                const res = await axiosInstance.get(`/post/profile/${User_profile.id}/public-posts/`);
                const data = res.data;
                const posts = data.results?.posts || [];

                if (posts.length === 0) break;

                if (round === 1) {
                    initialTotal = data.count || data.results?.total_count || posts.length;
                    pageSize = posts.length;
                }
                const totalPages = initialTotal > 0 ? Math.ceil(initialTotal / pageSize) : 1;

                setDeleteProgress((p) => ({ ...p, page: round, totalPages, total: initialTotal }));

                setDeleteLog((prev) => [
                    ...posts.map((post) => ({
                        id: post.id,
                        description: post.post_description,
                        thumbnail_url: post.thumbnail_url,
                        state: "queued",
                    })),
                    ...prev,
                ].slice(0, 500));

                for (const post of posts) {
                    if (deleteStopRef.current) break;
                    const result = await deleteOne(post);
                    if (result === "done") deleted += 1;
                    else if (result === "error") failed += 1;
                    setDeleteProgress((p) => ({ ...p, deleted, failed }));
                    await new Promise((r) => setTimeout(r, 400));
                }

                if (deleteStopRef.current) break;
                await new Promise((r) => setTimeout(r, 600));
            }
        } catch {
            setDeleteError("Something went wrong while deleting. Try again.");
        }

        setDeleteStatus(deleteStopRef.current ? STATUS.PAUSED : STATUS.DONE);
    };

    const stopDeleteScript = () => {
        deleteStopRef.current = true;
    };

    const resetDeleteScript = () => {
        setDeleteStatus(STATUS.IDLE);
        setDeleteLog([]);
        setDeleteProgress({ page: 0, totalPages: 0, deleted: 0, failed: 0, total: 0 });
        setDeleteError(null);
    };

    const isRunning = status === STATUS.RUNNING;
    const isDone = status === STATUS.DONE;
    const isStopped = status === STATUS.PAUSED;
    const isIdle = status === STATUS.IDLE;

    const progressPct = progress.total > 0
        ? Math.round((progress.downloaded / progress.total) * 100)
        : 0;

    const isDeleteRunning = deleteStatus === STATUS.RUNNING;
    const isDeleteDone = deleteStatus === STATUS.DONE;
    const isDeleteStopped = deleteStatus === STATUS.PAUSED;
    const isDeleteIdle = deleteStatus === STATUS.IDLE;

    const deleteProgressPct = deleteProgress.total > 0
        ? Math.round((deleteProgress.deleted / deleteProgress.total) * 100)
        : 0;

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21] flex flex-col overflow-hidden relative">
            {/* Header */}
            <div className="sticky top-0 z-10 flex-shrink-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/account/settings")}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-[22px] text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black">Manage My Videos</p>
                </div>
            </div>

            <div
                className="flex-1 overflow-y-auto px-3 pt-4 flex flex-col gap-4"
                style={{ paddingBottom: "calc(1.5rem + var(--footer-height, 0px))" }}
            >

                {/* ── Stats bar ── */}
                {!isIdle && (
                    <div className="grid grid-cols-4 gap-1 text-center">
                        {[
                            { label: "Page", value: `${progress.page}${progress.totalPages ? `/${progress.totalPages}` : ""}` },
                            { label: "Total", value: progress.total || "…" },
                            { label: "Done", value: progress.downloaded },
                            { label: "Failed", value: progress.failed },
                        ].map(({ label, value }) => (
                            <div key={label} className="bg-gray-100 dark:bg-gray-800 rounded-lg py-2">
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
                                <p className="text-[14px] font-bold text-black dark:text-white">{value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Progress bar ── */}
                {!isIdle && progress.total > 0 && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-[#BDE6FF] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                )}

                {/* ── Status label ── */}
                {isRunning && (
                    <p className="text-[12px] text-center text-gray-500 dark:text-gray-400 animate-pulse">
                        Fetching page {progress.page}{progress.totalPages ? ` of ${progress.totalPages}` : ""} — downloading videos…
                    </p>
                )}
                {isDone && (
                    <p className="text-[13px] text-center font-semibold text-green-600 dark:text-green-400">
                        All done! {progress.downloaded} downloaded{progress.failed > 0 ? `, ${progress.failed} failed` : ""}.
                    </p>
                )}
                {isStopped && (
                    <p className="text-[13px] text-center font-semibold text-yellow-600 dark:text-yellow-400">
                        Stopped at page {progress.page}. {progress.downloaded} downloaded so far.
                    </p>
                )}
                {error && (
                    <p className="text-[12px] text-red-500 text-center">{error}</p>
                )}

                {/* ── Action buttons ── */}
                {isIdle && (
                    <button
                        onClick={runScript}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#BDE6FF] text-black font-bold text-[14px]"
                    >
                        <Download size={16} />
                        Start — Load &amp; Download All Videos
                    </button>
                )}

                {isRunning && (
                    <button
                        onClick={stopScript}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-bold text-[14px]"
                    >
                        <StopCircle size={16} />
                        Stop
                    </button>
                )}

                {(isDone || isStopped) && (
                    <button
                        onClick={resetScript}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-black dark:text-white font-bold text-[14px]"
                    >
                        Reset
                    </button>
                )}

                {/* ── Activity log ── */}
                {log.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                            Activity log (latest first)
                        </p>
                        {log.map((entry) => (
                            <div
                                key={entry.id}
                                className="flex items-center gap-3 px-2 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800"
                            >
                                <img
                                    src={entry.thumbnail_url}
                                    alt=""
                                    className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-200 dark:bg-gray-700"
                                />
                                <p className="flex-1 text-[11px] text-black dark:text-white line-clamp-1">
                                    {entry.description || entry.id}
                                </p>
                                <div className="flex-shrink-0 w-5 flex justify-center">
                                    {entry.state === "queued" && (
                                        <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                                    )}
                                    {entry.state === "downloading" && (
                                        <Loader2 size={13} className="animate-spin text-blue-400" />
                                    )}
                                    {entry.state === "done" && (
                                        <CheckCircle2 size={14} className="text-green-500" />
                                    )}
                                    {entry.state === "error" && (
                                        <XCircle size={14} className="text-red-500" />
                                    )}
                                    {entry.state === "disabled" && (
                                        <span className="text-[9px] text-gray-400">off</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Divider ── */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

                {/* ── Delete All Videos section ── */}
                <div className="flex flex-col gap-3">
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg px-3 py-2">
                        <p className="text-[12px] font-semibold text-red-700 dark:text-red-400">Delete All My Videos</p>
                        <p className="text-[11px] text-red-500 dark:text-red-500/80 mt-0.5">
                            Permanently deletes every video from your account one by one. This cannot be undone.
                        </p>
                    </div>

                    {/* Delete stats bar */}
                    {!isDeleteIdle && (
                        <div className="grid grid-cols-4 gap-1 text-center">
                            {[
                                { label: "Page", value: `${deleteProgress.page}${deleteProgress.totalPages ? `/${deleteProgress.totalPages}` : ""}` },
                                { label: "Total", value: deleteProgress.total || "…" },
                                { label: "Deleted", value: deleteProgress.deleted },
                                { label: "Failed", value: deleteProgress.failed },
                            ].map(({ label, value }) => (
                                <div key={label} className="bg-red-50 dark:bg-red-950/30 rounded-lg py-2">
                                    <p className="text-[11px] text-red-400 dark:text-red-500">{label}</p>
                                    <p className="text-[14px] font-bold text-black dark:text-white">{value}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Delete progress bar */}
                    {!isDeleteIdle && deleteProgress.total > 0 && (
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-red-400 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${deleteProgressPct}%` }}
                            />
                        </div>
                    )}

                    {/* Delete status label */}
                    {isDeleteRunning && (
                        <p className="text-[12px] text-center text-red-500 dark:text-red-400 animate-pulse">
                            Deleting videos — page {deleteProgress.page}{deleteProgress.totalPages ? ` of ${deleteProgress.totalPages}` : ""}…
                        </p>
                    )}
                    {isDeleteDone && (
                        <p className="text-[13px] text-center font-semibold text-green-600 dark:text-green-400">
                            Done! {deleteProgress.deleted} deleted{deleteProgress.failed > 0 ? `, ${deleteProgress.failed} failed` : ""}.
                        </p>
                    )}
                    {isDeleteStopped && (
                        <p className="text-[13px] text-center font-semibold text-yellow-600 dark:text-yellow-400">
                            Stopped at page {deleteProgress.page}. {deleteProgress.deleted} deleted so far.
                        </p>
                    )}
                    {deleteError && (
                        <p className="text-[12px] text-red-500 text-center">{deleteError}</p>
                    )}

                    {/* Delete action buttons */}
                    {isDeleteIdle && (
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-bold text-[14px]"
                        >
                            <Trash2 size={16} />
                            Delete All Videos
                        </button>
                    )}

                    {isDeleteRunning && (
                        <button
                            onClick={stopDeleteScript}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-black dark:text-white font-bold text-[14px]"
                        >
                            <StopCircle size={16} />
                            Stop
                        </button>
                    )}

                    {(isDeleteDone || isDeleteStopped) && (
                        <button
                            onClick={resetDeleteScript}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-black dark:text-white font-bold text-[14px]"
                        >
                            Reset
                        </button>
                    )}

                    {/* Delete activity log */}
                    {deleteLog.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                                Delete log (latest first)
                            </p>
                            {deleteLog.map((entry) => (
                                <div
                                    key={entry.id}
                                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg border border-red-100 dark:border-red-900/30"
                                >
                                    <img
                                        src={entry.thumbnail_url}
                                        alt=""
                                        className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-200 dark:bg-gray-700"
                                    />
                                    <p className="flex-1 text-[11px] text-black dark:text-white line-clamp-1">
                                        {entry.description || entry.id}
                                    </p>
                                    <div className="flex-shrink-0 w-5 flex justify-center">
                                        {entry.state === "queued" && (
                                            <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                                        )}
                                        {entry.state === "deleting" && (
                                            <Loader2 size={13} className="animate-spin text-red-400" />
                                        )}
                                        {entry.state === "done" && (
                                            <CheckCircle2 size={14} className="text-green-500" />
                                        )}
                                        {entry.state === "error" && (
                                            <XCircle size={14} className="text-red-500" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Delete confirmation modal ── */}
            {showDeleteConfirm && (
                <div
                    className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                    onClick={() => setShowDeleteConfirm(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-xl p-6 mx-4 max-w-sm w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[20px] font-bold text-black dark:text-white text-center">Delete All Videos?</p>
                        <p className="text-[12px] text-gray-600 dark:text-gray-400 text-center mt-2">
                            This will permanently delete every video from your account one by one. This action cannot be undone.
                        </p>
                        <div className="flex gap-3 mt-5 justify-center">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-black dark:text-white text-[13px] font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    runDeleteScript();
                                }}
                                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[13px] font-semibold"
                            >
                                Yes, Delete All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DownloadMyVideos;
